import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuth from '../../../common/hooks/useAuth';
import useRefreshToken from '../../../common/hooks/useRefreshToken';
import { useAxios } from '../../../common/api/hooks/useAxios';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';

/**
 * Custom hook to check if user is eligible for demo upgrade offer
 * @returns {Object} { isChecking, isEligible, demoData }
 */
const useDemoEligibility = () => {
  const { auth } = useAuth();
  const refresh = useRefreshToken();
  const [searchParams] = useSearchParams();
  const [isChecking, setIsChecking] = useState(true);
  const [isEligible, setIsEligible] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const apiCallMadeRef = useRef(false);
  const lastCheckedAccessTokenRef = useRef(null);

  const [cancelAuth, responseDataAuth, errorAuth, loadedAuth, resetAuth, executeAPIAuth] = useAxiosWithAuth();
  const { cancel, responseData, error, loaded, reset, executeAPI } = useAxios();

  // First, check and refresh auth if needed
  // Skip auth check for normal logouts (no email_id in URL, no accessToken)
  useEffect(() => {
    let isMounted = true;
    const emailFromURL = searchParams.get('email_id');
    const hasAccessToken = auth?.accessToken && auth.accessToken.length > 0;
    
    // For normal logouts (no email_id, no accessToken), skip eligibility check immediately
    if (!hasAccessToken && !emailFromURL) {
      if (isMounted) {
        setIsChecking(false);
        setIsEligible(false);
        setAuthCheckComplete(true);
      }
      return;
    }
    
    const checkAuth = async () => {
      try {
        if (!auth?.accessToken && !authCheckComplete) {
          await refresh();
        }
        if (isMounted) {
          setAuthCheckComplete(true);
        }
      } catch (err) {
        if (isMounted) {
          setAuthCheckComplete(true);
        }
      }
    };

    checkAuth();
    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken, refresh, authCheckComplete, searchParams]);

  // Second: Make demo status API call once auth is ready
  useEffect(() => {
    let isMounted = true;

    if (!authCheckComplete) {
      return;
    }

    const hasAccessToken = auth?.accessToken && auth.accessToken.length > 0;
    const emailFromURL = searchParams.get('email_id');
    const emailFromAuth = auth?.email_id;
    const hasEmail = emailFromAuth || emailFromURL;
    const authIsReady = hasAccessToken || hasEmail;
    const currentAccessToken = auth?.accessToken || null;
    
    // Skip API call for normal logouts (no accessToken, no email_id in URL)
    // ensures we don't make unnecessary calls
    if (!hasAccessToken && !emailFromURL && !emailFromAuth) {
      return;
    }
    
    if (lastCheckedAccessTokenRef.current === null && currentAccessToken !== null && apiCallMadeRef.current) {
      apiCallMadeRef.current = false;
      lastCheckedAccessTokenRef.current = currentAccessToken;
    } else if (currentAccessToken !== null) {
      lastCheckedAccessTokenRef.current = currentAccessToken;
    }
    
    if (apiCallMadeRef.current && authIsReady && lastCheckedAccessTokenRef.current === currentAccessToken) {
      return;
    }
    
    if (!authIsReady && !apiCallMadeRef.current) {
      return;
    }
    
    if (apiCallMadeRef.current && !authIsReady && lastCheckedAccessTokenRef.current === null) {
      return;
    }

    const checkDemoStatus = async () => {
      try {
        if (hasAccessToken) {
          apiCallMadeRef.current = true;
          if (executeAPIAuth) {
            await executeAPIAuth('/chart/demo-status', 'GET', null);
          }
        } else {
          const email = emailFromURL || emailFromAuth;
          if (email && executeAPI) {
            apiCallMadeRef.current = true;
            await executeAPI(`/chart/demo-status?email_id=${email}`, 'GET', null);
          } else {
            if (isMounted) {
              setIsEligible(false);
              setIsChecking(false);
            }
          }
        }
      } catch (err) {
        console.error('Error checking demo status:', err);
        if (isMounted) {
          setIsEligible(false);
          setIsChecking(false);
        }
      }
    };

    checkDemoStatus();

    return () => {
      isMounted = false;
      if (auth?.accessToken && cancelAuth) {
        cancelAuth();
      } else if (cancel) {
        cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, searchParams, authCheckComplete]);

  // Handle response for logged in users
  useEffect(() => {
    if (loadedAuth && responseDataAuth) {
      const data = responseDataAuth;
      if (data?.success && data?.of_demo_offer_tr !== undefined && data?.of_demo_offer_tr !== null && data?.of_demo_offer_tr > 0) {
        setDemoData({
          minutesRemaining: data.of_demo_offer_tr,
          demoStatus: data.of_demo_status
        });
        setIsEligible(true);
      } else {
        setIsEligible(false);
      }
      setIsChecking(false);
    } else if (loadedAuth && errorAuth) {
      setIsEligible(false);
      setIsChecking(false);
    }
  }, [loadedAuth, responseDataAuth, errorAuth]);

  // Handle response for logged out users
  useEffect(() => {
    if (loaded && responseData) {
      const data = responseData;
      const emailFromURL = searchParams.get('email_id');
      
      // If user came from email/notification, show landing page even without timer
      if (emailFromURL) {
        setDemoData({
          minutesRemaining: data?.of_demo_offer_tr || 0,
          demoStatus: data?.of_demo_status || 'F'
        });
        if(data?.of_demo_offer_tr > 0)
          setIsEligible(true);
        else
          setIsEligible(false);
      } else if (data?.success && data?.of_demo_offer_tr !== undefined && data?.of_demo_offer_tr !== null && data?.of_demo_offer_tr > 0) {
        setDemoData({
          minutesRemaining: data.of_demo_offer_tr,
          demoStatus: data.of_demo_status
        });
        setIsEligible(true);
      } else {
        setIsEligible(false);
      }
      setIsChecking(false);
    } else if (loaded && error) {
      const emailFromURL = searchParams.get('email_id');
      if (emailFromURL) {
        setDemoData({
          minutesRemaining: 0,
          demoStatus: 'F'
        });
        setIsEligible(true);
      } else {
        setIsEligible(false);
      }
      setIsChecking(false);
    }
  }, [loaded, responseData, error, searchParams]);

  return { isChecking, isEligible, demoData };
};

export default useDemoEligibility;

